import { useMutation, useQuery } from '@tanstack/react-query';
import { API_V1 } from '../API/APIs/APIv1';
import useUserStore from '../stores/user-store';
import _ from 'lodash';
import Logger from 'logger';
import { ApiResponse } from '../features/lead-list-research/webmetics/webmetics.types';

// Original result format
interface OriginalResearchResult {
  name: string;
  description: string;
  website?: string;
}

// New result format with contact and company information
interface LeadListItem {
  contact: {
    name: string;
    role: string;
    email: string;
    phone: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
  };
  company: {
    name: string;
    website: string;
    industry: string;
    size: string;
    address?: {
      street?: string;
      postalCode?: string;
      city?: string;
      country?: string;
    };
  };
  leadReason?: string;
  location?: {
    city?: string;
    region?: string;
  };
}

// Union type for research results
export type ResearchResult = OriginalResearchResult | LeadListItem;

export interface LeadListResearchResponse {
  id: string;
  original_prompt: string;
  refined_prompt: string;
  research_results: ResearchResult[];
  savedListId?: string; // Optional property for auto-saved lists
}

export interface LeadListResearchFeedback {
  id: string;
  prompt: string;
  originalPrompt?: string; // Optional field for metadata tracking
}

// Storage interfaces
export interface StoredLeadList {
  _id: string;
  name: string;
  product: string;
  region: string;
  creator: string;
  leadData: ResearchResult[];
  isRefined: boolean;
  feedback?: string;
  tags?: string[];
  notes?: string;
  metadata: {
    createdAt: string;
    leadCount: number;
    autoSaved: boolean;
    originalPrompt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse {
  success: boolean;
  data: StoredLeadList[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// Lead list generation request parameters
export interface LeadListGenerateParams {
  product: string;
  region: string;
  autoSave?: boolean;
  prompt?: string; // User's input prompt for generation
  regionSplitInstructions?: string; // Optional instructions for splitting region
  subsearchInstructions?: string; // Optional instructions for subsearches
  peopleSearchInstructions?: string; // Optional instructions for people search
}

// Lead list refinement request parameters
export interface LeadListRefinementParams {
  id: string;
  prompt: string;
  originalPrompt?: string; // Optional field for metadata tracking
}

// Full research process parameters
export interface LeadListFullResearchParams {
  // Required parameters
  product?: string;            // Product to research (can be omitted when updating existing lists)
  region?: string;             // Region to target (can be omitted when updating existing lists)
  feedback?: string;           // Specific requirements/feedback

  // Optional parameters
  savedListId?: string;        // The ID of the existing LeadList to use for full research
  prompt?: string;             // Optional custom research instruction
  autoSave?: boolean;          // Whether to save results to database
  numRegions?: number;         // Optional number of subregions to divide the main region (range: 2-20, default: 6)
  regionSplitInstructions?: string; // Optional instructions on how the region should be divided
  numSubsearches?: number;     // Specify exactly how many subsearches to generate (range: 2-15, default: 5)
  numCompaniesPerSubregion?: number; // Number of companies per subregion (default: 20)
  subsearchInstructions?: string; // Instructions on how to generate subsearches rather than providing them directly
  peopleSearchInstructions?: string; // Optional instructions for people search
  numPeopleSearches?: number; // Number of people searches to generate
}

// Lead list storage request parameters
export interface LeadListStorageParams {
  name: string;
  product: string;
  region: string;
  leadData: ResearchResult[];
  tags?: string[];
  notes?: string;
  metadata?: {
    originalPrompt?: string;
    autoSave?: boolean;
  };
}

// Lead list update parameters
export interface LeadListUpdateParams {
  name?: string;
  product?: string;
  region?: string;
  leadData?: ResearchResult[];
  tags?: string[];
  notes?: string;
}

// Response structure when uploading/enriching a lead list (asynchronously processed)
export interface LeadListUploadResponse {
  success: boolean; // Indicates if the request was accepted
  status: string; // e.g. 'in_progress'
  savedListId: string; // ID of the stored list that is being enriched
  message: string; // Informational message for the user
}

// Filter parameters for getting lead lists
export interface LeadListFilterParams {
  product?: string;
  region?: string;
  tag?: string;
  isRefined?: boolean;
  page?: number;
  limit?: number;
}

// Lead list refinement with previous results request parameters
export interface LeadListRefinementWithPreviousParams {
  // Required parameters
  feedback: string;            // Refinement instructions

  // Optional parameters
  savedListId?: string;        // ID of existing list to refine
  product?: string;            // Override product if needed
  region?: string;             // Override region if needed
  prompt?: string;             // Override the original prompt if needed
  autoSave?: boolean;          // Whether to save results to database
  regionSplitInstructions?: string; // Optional instructions for splitting region
  subsearchInstructions?: string; // Optional instructions for subsearches
  peopleSearchInstructions?: string; // Optional instructions for people search
  numPeopleSearches?: number; // Number of people searches to generate
  additionalNotes?: string;   // Any extra instructions or context
}

// Lead list status response type
export interface LeadListStatusResponse {
  status: string; // e.g. 'pending', 'in_progress', 'done', 'failed'
  data: {
    id: string;
    status: string;
    name: string;
    product: string;
    region: string;
    createdAt: string;
    updatedAt: string;
    leadCount: number;
    partialLeads?: any[];
    // ...other fields as needed
  };
}

// Helper function to get auth headers
const getAuthHeaders = (token: string) => {
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

// API request functions with direct token usage
const getLeadListResult = async (id: string, token: string): Promise<LeadListResearchResponse> => {
  const response = await API_V1.get(`/leadList/${id}`, getAuthHeaders(token));
  return response.data;
};

const processRefinement = async (data: LeadListRefinementParams, token: string): Promise<LeadListResearchResponse> => {
  const response = await API_V1.post('/leadList/refine', data, getAuthHeaders(token));
  return response.data;
};

const processFullResearch = async (data: LeadListFullResearchParams, token: string): Promise<LeadListResearchResponse> => {
  const response = await API_V1.post('/leadList/process-full', data, getAuthHeaders(token));
  return response.data;
};

const generateLeadList = async (params: LeadListGenerateParams, token: string): Promise<LeadListResearchResponse> => {
  const response = await API_V1.post('/leadList/generate', params, getAuthHeaders(token));
  return response.data;
};

const getLeadLists = async (queryParams: URLSearchParams, token: string): Promise<PaginatedResponse> => {
  const response = await API_V1.get(`/leadList/storage?${queryParams}`, getAuthHeaders(token));
  return response.data;
};

const getLeadListById = async (id: string, token: string): Promise<StoredLeadList> => {
  const response = await API_V1.get(`/leadList/storage/${id}`, getAuthHeaders(token));
  return response.data;
};

const saveLeadList = async (params: LeadListStorageParams, token: string): Promise<StoredLeadList> => {
  const response = await API_V1.post('/leadList/storage', params, getAuthHeaders(token));
  return response.data;
};

const updateLeadList = async (id: string, updateData: LeadListUpdateParams, token: string): Promise<StoredLeadList> => {
  const response = await API_V1.put(`/leadList/storage/${id}`, updateData, getAuthHeaders(token));
  return response.data;
};

const deleteLeadList = async (id: string, token: string): Promise<{ success: boolean }> => {
  const response = await API_V1.delete(`/leadList/storage/${id}`, getAuthHeaders(token));
  return response.data;
};

const exportLeadListAsJSON = async (id: string, token: string): Promise<ResearchResult[]> => {
  const response = await API_V1.get(`/leadList/storage/${id}/export?format=json&includeExtendedFields=true`, getAuthHeaders(token));
  return response.data;
};

const processRefinementWithPrevious = async (data: LeadListRefinementWithPreviousParams, token: string): Promise<LeadListResearchResponse> => {
  const response = await API_V1.post('/leadList/process-refinement', data, getAuthHeaders(token));
  return response.data;
};

// Add enrichment API function
interface EnrichLeadListParams {
  id: string;
  customField?: string;
  customFieldPrompt?: string;
}
const enrichLeadListWithLinkedinUrl = async (params: EnrichLeadListParams, token: string): Promise<StoredLeadList> => {
  const { id, customField, customFieldPrompt } = params;
  const body: any = {};
  if (customField) body.customField = customField;
  if (customFieldPrompt) body.customFieldPrompt = customFieldPrompt;
  const response = await API_V1.post(`/leadList/storage/${id}/enrich-linkedin-url`, body, getAuthHeaders(token));
  return response.data;
};

// Get token safely with error handling
const getToken = (): string => {
  const token = useUserStore.getState().token;
  if (!token) {
    throw new Error('Authentication token is missing');
  }
  return token.accessToken;
};

// React Query hooks that use the API functions
export const useLeadListResult = (id: string) => {
  return useQuery<LeadListResearchResponse>({
    queryKey: ['leadListResult', id],
    queryFn: async () => {
      const token = getToken();
      return getLeadListResult(id, token);
    },
    enabled: !!id,
  });
};

export const useProcessRefinement = () => {
  return useMutation<LeadListResearchResponse, Error, LeadListRefinementParams>({
    mutationFn: async (data) => {
      const token = getToken();
      return processRefinement(data, token);
    },
  });
};

export const useProcessFullResearch = () => {
  return useMutation<LeadListResearchResponse, Error, LeadListFullResearchParams>({
    mutationFn: async (data) => {
      const token = getToken();
      return processFullResearch(data, token);
    },
  });
};

export const useGenerateLeadList = () => {
  return useMutation<LeadListResearchResponse, Error, LeadListGenerateParams>({
    mutationFn: async (params) => {
      const token = getToken();
      return generateLeadList(params, token);
    },
  });
};

export const useProcessRefinementWithPrevious = () => {
  return useMutation<LeadListResearchResponse, Error, LeadListRefinementWithPreviousParams>({
    mutationFn: async (data) => {
      const token = getToken();
      return processRefinementWithPrevious(data, token);
    },
  });
};

// Add enrichment mutation hook
export const useEnrichLeadListWithLinkedinUrl = () => {
  return useMutation<StoredLeadList, Error, EnrichLeadListParams>({
    mutationFn: async (params) => {
      const token = getToken();
      return enrichLeadListWithLinkedinUrl(params, token);
    },
  });
};

// Storage CRUD operations
export const useGetLeadLists = (filters: LeadListFilterParams = {}) => {
  const { product, region, tag, isRefined, page = 1, limit = 10 } = filters;

  return useQuery<PaginatedResponse>({
    queryKey: ['leadLists', { product, region, tag, isRefined, page, limit }],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (product) queryParams.append('product', product);
      if (region) queryParams.append('region', region);
      if (tag) queryParams.append('tag', tag);
      if (isRefined !== undefined) queryParams.append('isRefined', String(isRefined));
      queryParams.append('page', String(page));
      queryParams.append('limit', String(limit));

      const token = getToken();
      return getLeadLists(queryParams, token);
    },
  });
};

export const useGetLeadListById = (id: string) => {
  return useQuery<StoredLeadList>({
    queryKey: ['leadList', id],
    queryFn: async () => {
      const token = getToken();
      return getLeadListById(id, token);
    },
    enabled: !!id,
  });
};

export const useSaveLeadList = () => {
  return useMutation<StoredLeadList, Error, LeadListStorageParams>({
    mutationFn: async (params) => {
      const token = getToken();
      return saveLeadList(params, token);
    },
  });
};

// Upload and enrich lead list API function (async enrichment)
const uploadLeadList = async (params: LeadListStorageParams, token: string): Promise<LeadListUploadResponse> => {
  const response = await API_V1.post('/leadList/storage/upload', params, getAuthHeaders(token));
  return response.data;
};

// React Query hook for uploading and enriching lead list (returns minimal response, enrichment continues in background)
export const useUploadLeadList = () => {
  return useMutation<LeadListUploadResponse, Error, LeadListStorageParams>({
    mutationFn: async (params) => {
      const token = getToken();
      return uploadLeadList(params, token);
    },
  });
};

export const useUpdateLeadList = () => {
  return useMutation<StoredLeadList, Error, { id: string; updateData: LeadListUpdateParams }>({
    mutationFn: async ({ id, updateData }) => {
      const token = getToken();
      return updateLeadList(id, updateData, token);
    },
  });
};

export const useDeleteLeadList = () => {
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (id) => {
      const token = getToken();
      return deleteLeadList(id, token);
    },
  });
};

// Export methods
export const exportLeadListAsCSV = (id: string): void => {
  const token = getToken();
  window.location.href = `${API_V1.defaults.baseURL}/leadList/storage/${id}/export?format=csv&includeExtendedFields=true&token=${token}`;
};

export const useExportLeadListAsJSON = (id: string) => {
  return useQuery<ResearchResult[]>({
    queryKey: ['leadListExport', id, 'json'],
    queryFn: async () => {
      const token = getToken();
      return exportLeadListAsJSON(id, token);
    },
    enabled: false, // Query will not automatically run
  });
};

// New API function to add lead list contacts to a funnel
export interface AddToFunnelPayload {
  leadListId: string;
  funnelId: string;
  contactIndices?: number[];
}

export interface AddToFunnelResponse {
  success: boolean;
  message: string;
  funnelUpdateStatus: {
    success: boolean;
    countAdded: number;
    message: string;
  };
  leadListId: string;
  funnelId: string;
}

export const addLeadListContactsToFunnel = async (
  payload: AddToFunnelPayload,
  token: string
): Promise<ApiResponse<AddToFunnelResponse>> => {
  try {
    const response = await API_V1.post<AddToFunnelResponse>('/leadList/storage/add-to-funnel', payload, getAuthHeaders(token));
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace('Error adding lead list contacts to funnel!');
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

// API function to get lead list status
export const getLeadListStatus = async (id: string, token: string): Promise<LeadListStatusResponse> => {
  const response = await API_V1.get(`/leadList/status/${id}`, { headers: getAuthHeaders(token).headers });
  return response.data;
};

// React Query hook for polling lead list status
export const useLeadListStatus = (id: string) => {
  return useQuery<LeadListStatusResponse>({
    queryKey: ['leadListStatus', id],
    queryFn: async () => {
      const token = getToken();
      return getLeadListStatus(id, token);
    },
    enabled: !!id,
    refetchInterval: 15000, // 15 seconds
  });
};

// 1. Enrich selected contacts with BetterContact
export const enrichContactsWithBetterContact = async (
  id: string,
  contactIndices: number[],
  token: string
): Promise<{ success: boolean; message: string }> => {
  const response = await API_V1.post(
    `/leadList/storage/${id}/enrich-better-contacts`,
    { contactIndices },
    getAuthHeaders(token)
  );
  return response.data;
};

// 2. Poll enrichment status for selected contacts
export const getBetterContactEnrichmentStatus = async (
  id: string,
  contactIndices: number[],
  token: string
): Promise<{
  success: boolean;
  statuses: {
    index: number;
    enrichmentStatus: string;
    enrichmentError: string;
    email: string;
    phone: string;
  }[];
}> => {
  const indicesParam = contactIndices.join(',');
  const response = await API_V1.get(
    `/leadList/storage/${id}/enrichment-status?contactIndices=${indicesParam}`,
    { headers: getAuthHeaders(token).headers }
  );
  return response.data;
};

// React Query hooks
export const useEnrichContactsWithBetterContact = () => {
  return useMutation<{ success: boolean; message: string }, Error, { id: string; contactIndices: number[] }>(
    {
      mutationFn: async ({ id, contactIndices }) => {
        const token = getToken();
        return enrichContactsWithBetterContact(id, contactIndices, token);
      },
    }
  );
};

export const useBetterContactEnrichmentStatus = (id: string, contactIndices: number[], enabled: boolean) => {
  return useQuery({
    queryKey: ['betterContactEnrichmentStatus', id, contactIndices],
    queryFn: async () => {
      const token = getToken();
      return getBetterContactEnrichmentStatus(id, contactIndices, token);
    },
    enabled: enabled && !!id && contactIndices.length > 0,
    refetchInterval: 3000, // Poll every 3 seconds
  });
};

// --- MATRIX PROMPT APPROVAL ENDPOINTS ---

// Types for subprompt approval
export interface ApprovedSubregion {
  name: string;
  description: string;
}
export interface ApprovedSubsearch {
  name: string;
  instruction: string;
}
export interface ApprovedPeopleSearch {
  name: string;
  instruction: string;
}

export interface MatrixSubpromptsResponse {
  status: string;
  data: {
    id: string;
    approvedSubregions: ApprovedSubregion[];
    approvedSubsearches: ApprovedSubsearch[];
    approvedPeopleSearches: ApprovedPeopleSearch[];
  };
}

// POST /leadList/matrix (returns only subprompts)
const generateMatrixSubprompts = async (params: any, token: string): Promise<MatrixSubpromptsResponse> => {
  const response = await API_V1.post('/leadList/matrix', params, getAuthHeaders(token));
  return response.data;
};

// PUT /leadList/:id/prompts (updates only subprompts)
const updateMatrixSubprompts = async (
  id: string,
  approvedSubregions: ApprovedSubregion[],
  approvedSubsearches: ApprovedSubsearch[],
  approvedPeopleSearches: ApprovedPeopleSearch[],
  token: string
): Promise<MatrixSubpromptsResponse> => {
  const response = await API_V1.put(`/leadList/${id}/prompts`, { approvedSubregions, approvedSubsearches, approvedPeopleSearches }, getAuthHeaders(token));
  return response.data;
};

// React Query hooks
export const useGenerateMatrixSubprompts = () => {
  return useMutation<MatrixSubpromptsResponse, Error, any>({
    mutationFn: async (params) => {
      const token = getToken();
      return generateMatrixSubprompts(params, token);
    },
  });
};

export const useUpdateMatrixSubprompts = () => {
  return useMutation<MatrixSubpromptsResponse, Error, {
    id: string;
    approvedSubregions: ApprovedSubregion[];
    approvedSubsearches: ApprovedSubsearch[];
    approvedPeopleSearches: ApprovedPeopleSearch[];
  }>({
    mutationFn: async ({ id, approvedSubregions, approvedSubsearches, approvedPeopleSearches }) => {
      const token = getToken();
      return updateMatrixSubprompts(
        id,
        approvedSubregions,
        approvedSubsearches,
        approvedPeopleSearches,
        token
      );
    },
  });
};

// 3. Remove selected contacts from a lead list
export const removeContactsFromLeadList = async (
  id: string,
  indexes: number[],
  token: string
): Promise<{ success: boolean; message: string; removedCount: number; leadList: StoredLeadList }> => {
  // Note: axios DELETE does not support a request body as the second parameter directly,
  // so we need to pass the payload in the `data` field of the config object.
  const response = await API_V1.delete(`/leadList/storage/${id}/contacts`, {
    ...getAuthHeaders(token),
    data: { indexes },
  });
  return response.data;
};

// React-Query hook for the removal mutation
export const useRemoveContactsFromLeadList = () => {
  return useMutation<
    { success: boolean; message: string; removedCount: number; leadList: StoredLeadList },
    Error,
    { id: string; indexes: number[] }
  >({
    mutationFn: async ({ id, indexes }) => {
      const token = getToken();
      return removeContactsFromLeadList(id, indexes, token);
    },
  });
};